import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { localPeriod } from '../lib/budget';
import { useAuth } from '../lib/auth-context';
import { useLiveReload } from '../lib/live-context';
import { ruleSummary, todos, WEEKDAYS, type RecurringRule } from '../lib/todos';

export function Recurring() {
  const { state } = useAuth();
  const me = state.status === 'signedIn' ? state.me : null;
  const [rules, setRules] = useState<RecurringRule[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const r = await todos.rules();
    setRules(r.rules);
  }, []);

  useEffect(() => {
    load().catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load.'));
  }, [load]);

  useLiveReload(() => {
    void load().catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load.'));
  });

  if (!me) return null;

  return (
    <main className="page">
      <Link className="back-link" to="/todos">
        ← Todos
      </Link>
      <h1>Recurring</h1>
      <p className="hint">Rules create an open todo when the next date arrives.</p>
      {error && <p className="error">{error}</p>}

      <RuleForm myId={me.user.id} partner={me.partner} onSaved={load} />

      {rules === null ? (
        <p className="hint" aria-busy="true">
          Loading…
        </p>
      ) : rules.length === 0 ? (
        <p className="hint">No repeating tasks yet.</p>
      ) : (
        <ul className="list">
          {rules.map((rule) => (
            <li key={rule.id} className={rule.active ? undefined : 'muted-text'}>
              <div>
                <strong>
                  {rule.title}
                  {!rule.active && <span className="badge muted">paused</span>}
                </strong>
                <span className="meta">
                  {ruleSummary(rule)}
                  {rule.assignedTo ? ` · ${rule.assignedTo.displayName}` : ' · Anyone'}
                  {` · next ${rule.nextRunDate}`}
                </span>
              </div>
              <div className="actions">
                <button
                  className="secondary"
                  onClick={() =>
                    void todos.updateRule(rule.id, { active: !rule.active }).then(load)
                  }
                >
                  {rule.active ? 'Pause' : 'Resume'}
                </button>
                <button
                  className="danger"
                  onClick={() => {
                    if (!window.confirm(`Delete the “${rule.title}” rule? Existing todos stay.`)) return;
                    void todos.removeRule(rule.id).then(load);
                  }}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

function RuleForm({
  myId,
  partner,
  onSaved,
}: {
  myId: number;
  partner: { id: number; displayName: string } | null;
  onSaved: () => Promise<void>;
}) {
  const today = (() => {
    const { year, month } = localPeriod();
    const d = new Date();
    return `${year}-${String(month).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  const [title, setTitle] = useState('');
  const [frequency, setFrequency] = useState<RecurringRule['frequency']>('weekly');
  const [interval, setInterval] = useState('1');
  const [weekdays, setWeekdays] = useState<number[]>([1]);
  const [assigned, setAssigned] = useState('');
  const [startsOn, setStartsOn] = useState(today);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggleDay(id: number) {
    setWeekdays((current) =>
      current.includes(id) ? current.filter((d) => d !== id) : [...current, id].sort((a, b) => a - b),
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const n = Number(interval);
    if (!Number.isInteger(n) || n < 1) {
      setError('Interval must be at least 1.');
      return;
    }
    if (frequency === 'weekly' && weekdays.length === 0) {
      setError('Pick at least one weekday.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await todos.addRule({
        title: title.trim(),
        frequency,
        interval: n,
        weekdays: frequency === 'weekly' ? weekdays : undefined,
        assignedToUserId: assigned === '' ? null : Number(assigned),
        startsOn,
      });
      setTitle('');
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel" onSubmit={submit}>
      <h2>Add a rule</h2>
      <label>
        Title
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={120}
          placeholder="Take out trash"
          required
        />
      </label>
      <div className="todo-add-row">
        <label>
          Repeat
          <select
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as RecurringRule['frequency'])}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </label>
        <label>
          Every
          <input
            value={interval}
            onChange={(e) => setInterval(e.target.value)}
            inputMode="numeric"
            min={1}
          />
        </label>
      </div>
      {frequency === 'weekly' && (
        <div className="weekday-picks">
          {WEEKDAYS.map((d) => (
            <button
              key={d.id}
              type="button"
              className={weekdays.includes(d.id) ? 'filter active' : 'filter'}
              onClick={() => toggleDay(d.id)}
            >
              {d.label}
            </button>
          ))}
        </div>
      )}
      <div className="todo-add-row">
        <label>
          Assign
          <select value={assigned} onChange={(e) => setAssigned(e.target.value)}>
            <option value="">Anyone</option>
            <option value={myId}>Me</option>
            {partner && <option value={partner.id}>{partner.displayName}</option>}
          </select>
        </label>
        <label>
          Starts
          <input
            type="date"
            value={startsOn}
            onChange={(e) => setStartsOn(e.target.value)}
            required
          />
        </label>
      </div>
      {error && <p className="error">{error}</p>}
      <button className="primary" disabled={busy || !title.trim()}>
        Add rule
      </button>
    </form>
  );
}
